import { state, type LibraryEntry } from "../lib/state";
import { apiRequest } from "../lib/api";
import { showToast, formatError } from "../lib/dom";
import { setActiveScreen } from "../lib/router";
import { trackEvent } from "../lib/analytics";
import {
  PERSON_SAMPLE_LIBRARY,
  GARMENT_SAMPLE_LIBRARY,
  addToLibrary,
  fileToDataUrl,
} from "../lib/storage";
import { refreshBillingState } from "./billing";
import { openClothesCatalog } from "./clothesCatalog";
import { fetchWebClothesList } from "../lib/remoteConfig";
import { resolveStoragePathToUrl, pathToDataUrl } from "../lib/firebaseStorage";
import type { ClothesItem } from "../types/clothes";
import type { CreateProjectRequest, CreateProjectResponse } from "../types/api";

const TRENDING_LIMIT = 10;

type GarmentTab = "trending" | "custom";
let activeGarmentTab: GarmentTab = "trending";

let trendingObserver: IntersectionObserver | null = null;
let trendingRendered = false;
let trendingItems: ClothesItem[] = [];

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function syncPhotoCard(target: "outfit" | "hair"): void {
  const card = document.querySelector<HTMLElement>(`[data-photo-card="${target}"]`);
  if (!card) return;
  const empty = card.querySelector<HTMLElement>('[data-photo-state="empty"]');
  const filled = card.querySelector<HTMLElement>('[data-photo-state="filled"]');
  const thumb = card.querySelector<HTMLImageElement>(".photo-thumb");
  const entry = target === "outfit" ? state.selectedOutfitPerson : state.selectedHairPerson;
  const hasPhoto = Boolean(entry?.dataUrl);

  if (empty) empty.hidden = hasPhoto;
  if (filled) filled.hidden = !hasPhoto;
  if (thumb) {
    if (hasPhoto && entry) thumb.src = entry.dataUrl;
    else thumb.removeAttribute("src");
  }
}

export function syncPreviewEmptyStates(): void {
  syncPhotoCard("outfit");
  syncPhotoCard("hair");
  renderSelectedGarments();
}

interface CardOptions {
  selected?: boolean;
  garment?: boolean;
}

function createLibraryCard(entry: LibraryEntry, options: CardOptions = {}): HTMLButtonElement {
  const { selected = false, garment = false } = options;
  const button = document.createElement("button");
  button.type = "button";
  button.className = `library-card ${garment ? "is-garment" : ""} ${selected ? "is-selected" : ""}`.trim();
  button.innerHTML = `
    <img class="library-thumb" src="${entry.dataUrl}" alt="${escapeHtml(entry.name)}" />
    <div class="library-card-copy">
      <span class="library-badge">${entry.source === "sample" ? "Mock" : "Recent"}</span>
      <strong class="library-card-title">${escapeHtml(entry.name)}</strong>
    </div>
  `;
  return button;
}

function renderLibraryGrid(
  container: HTMLElement | null,
  items: LibraryEntry[],
  emptyMessage: string,
  builder: (item: LibraryEntry) => HTMLElement,
): void {
  if (!container) return;
  container.innerHTML = "";
  if (!items.length) {
    const empty = document.createElement("div");
    empty.className = "library-empty";
    empty.textContent = emptyMessage;
    container.appendChild(empty);
    return;
  }

  items.forEach(item => {
    container.appendChild(builder(item));
  });
}

export function renderPersonLibraries(): void {
  const outfitPersonLibrary = document.getElementById("outfit-person-library");
  const hairPersonLibrary = document.getElementById("hair-person-library");
  const combined: LibraryEntry[] = import.meta.env.DEV
    ? [...state.personLibrary, ...PERSON_SAMPLE_LIBRARY]
    : [...state.personLibrary];

  renderLibraryGrid(
    outfitPersonLibrary,
    combined,
    "No saved people yet.",
    item => {
      const button = createLibraryCard(item, {
        selected: state.selectedOutfitPerson?.dataUrl === item.dataUrl,
      });
      button.addEventListener("click", () => {
        state.selectedOutfitPerson = item;
        trackEvent("studio_person_image_uploaded", { source: "library" });
        syncPreviewEmptyStates();
        renderPersonLibraries();
      });
      return button;
    },
  );

  renderLibraryGrid(
    hairPersonLibrary,
    combined,
    "No saved people yet.",
    item => {
      const button = createLibraryCard(item, {
        selected: state.selectedHairPerson?.dataUrl === item.dataUrl,
      });
      button.addEventListener("click", () => {
        state.selectedHairPerson = item;
        trackEvent("hair_person_image_uploaded", { source: "library" });
        syncPreviewEmptyStates();
        renderPersonLibraries();
      });
      return button;
    },
  );
}

export function renderGarmentLibrary(): void {
  const outfitGarmentLibrary = document.getElementById("outfit-garment-library");
  const combined: LibraryEntry[] = import.meta.env.DEV
    ? [...state.garmentLibrary, ...GARMENT_SAMPLE_LIBRARY]
    : [...state.garmentLibrary];

  renderLibraryGrid(
    outfitGarmentLibrary,
    combined,
    "No saved garments yet.",
    item => {
      const isSelected = state.selectedOutfitGarments.some(
        entry => entry.dataUrl === item.dataUrl,
      );
      const button = createLibraryCard(item, { selected: isSelected, garment: true });
      button.addEventListener("click", () => {
        toggleGarmentSelection(item);
      });
      return button;
    },
  );
}

function toggleGarmentSelection(item: LibraryEntry): void {
  const exists = state.selectedOutfitGarments.some(
    entry => entry.dataUrl === item.dataUrl,
  );
  if (exists) {
    state.selectedOutfitGarments = state.selectedOutfitGarments.filter(
      entry => entry.dataUrl !== item.dataUrl,
    );
  } else if (state.selectedOutfitGarments.length < 2) {
    state.selectedOutfitGarments = [...state.selectedOutfitGarments, item];
  } else {
    const second = state.selectedOutfitGarments[1];
    state.selectedOutfitGarments = second ? [second, item] : [item];
    showToast("Up to 2 garments. Replaced the first.", true);
  }
  if (!exists) {
    trackEvent("studio_garment_selected", {
      source: "custom",
      total_selected: state.selectedOutfitGarments.length,
    });
  }
  renderGarmentLibrary();
  renderTrendingSelectionState();
  renderSelectedGarments();
}

export function renderSelectedGarments(): void {
  const host = document.getElementById("outfit-selected-garments");
  if (!host) return;
  host.innerHTML = "";
  const entries = state.selectedOutfitGarments;
  host.hidden = entries.length === 0;
  if (!entries.length) return;

  entries.forEach(entry => {
    const tile = document.createElement("div");
    tile.className = "selected-garment";
    tile.innerHTML = `
      <img src="${entry.dataUrl}" alt="${escapeHtml(entry.name)}" />
      <button class="selected-garment-remove" type="button" aria-label="Remove garment">×</button>
    `;
    tile.querySelector("button")?.addEventListener("click", () => {
      state.selectedOutfitGarments = state.selectedOutfitGarments.filter(
        e => e.dataUrl !== entry.dataUrl,
      );
      renderGarmentLibrary();
      renderTrendingSelectionState();
      renderSelectedGarments();
    });
    host.appendChild(tile);
  });
}

function applyGarmentTabUI(): void {
  const buttons = document.querySelectorAll<HTMLElement>("[data-garment-tab]");
  const panes = document.querySelectorAll<HTMLElement>("[data-garment-pane]");
  buttons.forEach(button => {
    const isActive = button.dataset.garmentTab === activeGarmentTab;
    button.classList.toggle("is-active", isActive);
    button.setAttribute("aria-selected", isActive ? "true" : "false");
  });
  panes.forEach(pane => {
    pane.hidden = pane.dataset.garmentPane !== activeGarmentTab;
  });
}

function trendingItemSelected(item: ClothesItem): boolean {
  return state.selectedOutfitGarments.some(entry => entry.id === item.id);
}

function renderTrendingSelectionState(): void {
  const cards = document.querySelectorAll<HTMLElement>("#outfit-trending-strip .trending-card");
  cards.forEach(card => {
    const id = card.dataset.itemId;
    const item = trendingItems.find(t => t.id === id);
    if (!item) return;
    card.classList.toggle("is-selected", trendingItemSelected(item));
  });
}

function ensureTrendingObserver(): IntersectionObserver {
  if (trendingObserver) return trendingObserver;
  trendingObserver = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const card = entry.target as HTMLElement;
        const path = card.dataset.storagePath;
        const img = card.querySelector<HTMLImageElement>("img");
        if (!path || !img || img.src) return;

        resolveStoragePathToUrl(path)
          .then(url => {
            if (!img.src) img.src = url;
          })
          .catch(error => {
            console.error("Failed to resolve trending image", path, error);
          });

        trendingObserver?.unobserve(card);
      });
    },
    { rootMargin: "200px" },
  );
  return trendingObserver;
}

async function pickTrendingItems(): Promise<ClothesItem[]> {
  const data = await fetchWebClothesList();
  const merged = [...data.female, ...data.male];
  return merged.slice(0, TRENDING_LIMIT);
}

export async function renderTrendingStrip(): Promise<void> {
  const strip = document.getElementById("outfit-trending-strip");
  if (!strip) return;
  if (trendingRendered) {
    renderTrendingSelectionState();
    return;
  }
  trendingRendered = true;
  strip.innerHTML = '<div class="trending-skeleton">Loading…</div>';

  let items: ClothesItem[] = [];
  try {
    items = await pickTrendingItems();
  } catch (error) {
    console.error("Failed to load trending garments", error);
    strip.innerHTML = '<div class="trending-empty">Couldn\'t load picks. Try again later.</div>';
    trendingRendered = false;
    return;
  }

  trendingItems = items;
  if (!items.length) {
    strip.innerHTML = '<div class="trending-empty">No trending items yet.</div>';
    return;
  }

  strip.innerHTML = "";
  strip.removeAttribute("aria-busy");
  const observer = ensureTrendingObserver();

  items.forEach(item => {
    const card = document.createElement("button");
    card.type = "button";
    card.className = "trending-card";
    card.dataset.itemId = item.id;
    card.dataset.storagePath = item.imageReference;
    card.setAttribute("role", "listitem");
    card.innerHTML = `
      <img alt="${escapeHtml(item.name)}" loading="lazy" />
      <span class="trending-card-name">${escapeHtml(item.name)}</span>
    `;
    card.addEventListener("click", () => {
      void onTrendingCardClick(item, card);
    });
    strip.appendChild(card);
    observer.observe(card);
  });

  renderTrendingSelectionState();
}

async function onTrendingCardClick(item: ClothesItem, card: HTMLElement): Promise<void> {
  if (trendingItemSelected(item)) {
    state.selectedOutfitGarments = state.selectedOutfitGarments.filter(
      entry => entry.id !== item.id,
    );
    renderTrendingSelectionState();
    renderSelectedGarments();
    return;
  }

  if (state.selectedOutfitGarments.length >= 2) {
    showToast("Up to 2 garments. Remove one first.", true);
    return;
  }

  card.classList.add("is-loading");
  try {
    const dataUrl = await pathToDataUrl(item.imageReference);
    const entry: LibraryEntry = {
      id: item.id,
      name: item.name,
      dataUrl,
      source: "catalog",
    };
    addToLibrary("garment", [entry]);
    state.selectedOutfitGarments = [...state.selectedOutfitGarments, entry];
    trackEvent("studio_garment_selected", {
      source: "trending",
      total_selected: state.selectedOutfitGarments.length,
      clothing_id: item.id,
    });
    renderGarmentLibrary();
    renderTrendingSelectionState();
    renderSelectedGarments();
  } catch (error) {
    showToast(formatError(error), true);
  } finally {
    card.classList.remove("is-loading");
  }
}

export async function prefetchTrendingThumbnails(): Promise<void> {
  try {
    const items = await pickTrendingItems();
    await Promise.allSettled(
      items.map(item => resolveStoragePathToUrl(item.imageReference)),
    );
  } catch {
    // Best effort; the strip will render lazily later.
  }
}

export function renderStyleSelection(): void {
  const styleButtons = document.querySelectorAll<HTMLElement>(".outfit-style");
  styleButtons.forEach(button => {
    button.classList.toggle("is-active", button.dataset.style === state.selectedOutfitStyle);
  });
}

function composeOutfitPrompt(): string {
  const styleButtons = document.querySelectorAll<HTMLElement>(".outfit-style");
  let stylePrompt = "";
  styleButtons.forEach(button => {
    if (button.dataset.style === state.selectedOutfitStyle) {
      stylePrompt = button.dataset.prompt || "";
    }
  });
  const promptInput = document.getElementById("outfit-prompt") as HTMLTextAreaElement | null;
  const customPrompt = promptInput?.value.trim() || "";
  if (stylePrompt && customPrompt) {
    return `${stylePrompt} Additional style direction: ${customPrompt}`;
  }
  return stylePrompt || customPrompt || "";
}

export async function useUploadedPerson(file: File, target: "outfit" | "hair"): Promise<void> {
  const dataUrl = await fileToDataUrl(file);
  const entry: LibraryEntry = {
    id: `person-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: file.name.replace(/\.[^.]+$/, "") || "Recent person",
    dataUrl,
    source: "upload",
  };

  addToLibrary("person", [entry]);
  if (target === "outfit") {
    state.selectedOutfitPerson = entry;
    trackEvent("studio_person_image_uploaded", { source: "upload" });
  } else {
    state.selectedHairPerson = entry;
    trackEvent("hair_person_image_uploaded", { source: "upload" });
  }

  renderPersonLibraries();
  syncPreviewEmptyStates();
}

export async function useUploadedGarments(files: File[]): Promise<void> {
  const limited = files.slice(0, 2);
  const dataUrls = await Promise.all(limited.map(fileToDataUrl));
  const entries: LibraryEntry[] = dataUrls.map((dataUrl, index) => {
    const file = limited[index];
    return {
      id: `garment-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`,
      name: file?.name.replace(/\.[^.]+$/, "") || `Garment ${index + 1}`,
      dataUrl,
      source: "upload",
    };
  });

  addToLibrary("garment", entries);
  state.selectedOutfitGarments = entries;
  if (entries.length) {
    trackEvent("custom_garment_created", { count: entries.length });
    trackEvent("studio_garment_selected", {
      source: "custom",
      total_selected: entries.length,
    });
  }
  renderGarmentLibrary();
  renderTrendingSelectionState();
  renderSelectedGarments();
}

export function clearSelectedPerson(target: "outfit" | "hair"): void {
  if (target === "outfit") state.selectedOutfitPerson = null;
  else state.selectedHairPerson = null;
  syncPreviewEmptyStates();
  renderPersonLibraries();
}

async function submitOutfitProject(event: Event): Promise<void> {
  event.preventDefault();

  if (!state.user || !state.idToken) {
    showToast("Sign in first.", true);
    setActiveScreen("account");
    return;
  }

  if (!state.selectedOutfitPerson?.dataUrl) {
    showToast("Upload your photo first.", true);
    return;
  }

  const outfitPrompt = composeOutfitPrompt();
  const hasGarments = state.selectedOutfitGarments.length > 0;

  if (!hasGarments && !outfitPrompt) {
    showToast("Pick a garment, a style, or write a prompt.", true);
    return;
  }

  const submitButton = document.getElementById("outfit-submit-button") as HTMLButtonElement | null;
  if (submitButton) {
    submitButton.disabled = true;
    submitButton.textContent = "Generating…";
  }

  const isCustomOutfit = state.selectedOutfitGarments.some(
    entry => entry.source === "upload",
  );
  const promptInput = document.getElementById("outfit-prompt") as HTMLTextAreaElement | null;
  const hasCustomPrompt = Boolean(promptInput?.value.trim());

  trackEvent("outfit_generation_started", {
    is_custom_outfit: isCustomOutfit,
    garment_count: state.selectedOutfitGarments.length,
    has_custom_prompt: hasCustomPrompt,
    style_id: state.selectedOutfitStyle || undefined,
  });

  try {
    const body: CreateProjectRequest = {
      inputImage: state.selectedOutfitPerson.dataUrl,
      outfitImages: hasGarments
        ? state.selectedOutfitGarments.map(entry => entry.dataUrl)
        : undefined,
      outfitPrompt: outfitPrompt || undefined,
    };

    await apiRequest<CreateProjectResponse>("/api/create-project", {
      method: "POST",
      body: JSON.stringify(body),
    });

    trackEvent("outfit_generated", { is_custom_outfit: isCustomOutfit });
    showToast(
      "Your outfit is being generated. We'll show it in My Closet when it's ready.",
    );
    await refreshBillingState();
    setActiveScreen("closet");
  } catch (error: unknown) {
    const code =
      typeof error === "object" && error !== null && "code" in error
        ? String((error as { code?: unknown }).code || "")
        : undefined;
    trackEvent("outfit_generation_failure", {
      error_code: code,
      error_message: error instanceof Error ? error.message : String(error),
    });
    showToast(formatError(error), true);
  } finally {
    if (submitButton) {
      submitButton.disabled = false;
      submitButton.textContent = "Try On";
    }
  }
}

async function handlePersonInputChange(input: HTMLInputElement): Promise<void> {
  const file = input.files?.[0];
  if (!file) return;
  await useUploadedPerson(file, "outfit");
  input.value = "";
}

async function handleHairPersonInputChange(input: HTMLInputElement): Promise<void> {
  const file = input.files?.[0];
  if (!file) return;
  await useUploadedPerson(file, "hair");
  input.value = "";
}

export function bind(): void {
  const outfitForm = document.getElementById("outfit-form") as HTMLFormElement | null;
  const outfitPersonInput = document.getElementById("outfit-person") as HTMLInputElement | null;
  const outfitPersonCamera = document.getElementById("outfit-person-camera") as HTMLInputElement | null;
  const outfitGarmentsInput = document.getElementById("outfit-garments") as HTMLInputElement | null;
  const outfitPromptInput = document.getElementById("outfit-prompt") as HTMLTextAreaElement | null;
  const outfitPresetButtons = document.querySelectorAll<HTMLElement>(".outfit-preset");
  const outfitStyleButtons = document.querySelectorAll<HTMLElement>(".outfit-style");
  const browseCollectionButton = document.getElementById(
    "outfit-browse-collection",
  ) as HTMLButtonElement | null;

  outfitPersonInput?.addEventListener("change", () => {
    void handlePersonInputChange(outfitPersonInput);
  });
  outfitPersonCamera?.addEventListener("change", () => {
    void handlePersonInputChange(outfitPersonCamera);
  });

  document.querySelectorAll<HTMLInputElement>('[data-photo-replace]').forEach(input => {
    const target = input.dataset.photoReplace === "hair" ? "hair" : "outfit";
    input.addEventListener("change", async () => {
      const file = input.files?.[0];
      if (!file) return;
      await useUploadedPerson(file, target);
      input.value = "";
    });
  });

  document.querySelectorAll<HTMLElement>('[data-photo-clear]').forEach(button => {
    const target = button.dataset.photoClear === "hair" ? "hair" : "outfit";
    button.addEventListener("click", () => clearSelectedPerson(target));
  });

  outfitGarmentsInput?.addEventListener("change", async () => {
    const files = [...(outfitGarmentsInput.files || [])];
    if (!files.length) return;
    await useUploadedGarments(files);
    outfitGarmentsInput.value = "";
  });

  document.querySelectorAll<HTMLElement>("[data-garment-tab]").forEach(button => {
    button.addEventListener("click", () => {
      const next = button.dataset.garmentTab as GarmentTab | undefined;
      if (next !== "trending" && next !== "custom") return;
      if (next === activeGarmentTab) return;
      activeGarmentTab = next;
      applyGarmentTabUI();
      if (next === "trending") void renderTrendingStrip();
    });
  });

  browseCollectionButton?.addEventListener("click", () => {
    openClothesCatalog(entries => {
      const merged = [...state.selectedOutfitGarments];
      entries.forEach(entry => {
        const exists = merged.some(e => e.dataUrl === entry.dataUrl);
        if (!exists && merged.length < 2) merged.push(entry);
      });
      state.selectedOutfitGarments = merged.slice(0, 2);
      addToLibrary("garment", entries);
      trackEvent("studio_garment_selected", {
        source: "catalog",
        total_selected: state.selectedOutfitGarments.length,
      });
      renderGarmentLibrary();
      renderTrendingSelectionState();
      renderSelectedGarments();
    });
  });

  outfitPresetButtons.forEach(button => {
    button.addEventListener("click", () => {
      if (outfitPromptInput) {
        outfitPromptInput.value = button.dataset.prompt || "";
        outfitPromptInput.focus();
      }
    });
  });

  outfitStyleButtons.forEach(button => {
    button.addEventListener("click", () => {
      const nextStyle = button.dataset.style || "";
      const isSelecting = state.selectedOutfitStyle !== nextStyle;
      state.selectedOutfitStyle = isSelecting ? nextStyle : "";
      if (isSelecting && nextStyle) {
        trackEvent("studio_style_selected", { style_id: nextStyle });
      }
      renderStyleSelection();
    });
  });

  outfitForm?.addEventListener("submit", event => {
    void submitOutfitProject(event);
  });

  applyGarmentTabUI();
  void renderTrendingStrip();
}

export { handleHairPersonInputChange };
